import React from 'react';
import {
  FaUserTie,
  FaUsers,
  FaHourglassHalf,
  FaCheckDouble,
  FaCalendarCheck,
  FaClipboardCheck,
  FaProjectDiagram,
  FaBuilding,
  FaExclamationTriangle,
  FaChartLine,
  FaMapMarkerAlt,
  FaCog,
  FaFileAlt,
} from 'react-icons/fa';
import { TechnicalAssociateDashboard } from '../../../types/dashboard';
import { StatCard, SectionCard, MiniStat, StatusBadge, DataTable, formatDate } from './ui';
import { ApprovalsSection } from './approvals-section';
import { BarChart, ProgressRing } from './charts';

/**
 * Technical Associate view — extended to match the "Dashboard 140626 Mukul" (TA tab) spec.
 *
 * Replace the existing `TechnicalAssociateView` export inside `role-dashboards.tsx` with this
 * (keep it in the same `components/role-dashboards` folder so './ui' / './charts' resolve).
 * New fields are read via `(data as any)` so the view still renders if the backend isn't
 * updated yet. Add the new fields to `TechnicalAssociateDashboard` in types/dashboard.ts for
 * full typing when convenient.
 */

const toNumber = (v: unknown) => {
  if (typeof v === 'number') return v;
  if (typeof v === 'string') {
    const n = Number(v);
    return Number.isFinite(n) ? n : 0;
  }
  return 0;
};

// column label overrides so headers read like the spec sheet
const PENDING_LABELS: Record<string, string> = {
  engineer_assigned: 'Engineer Assigned',
  field_work_in_progress: 'Field Work In Progress',
  data_uploaded: 'Site Data WIP',
  field_work_completed: 'Field Data Upload Pending',
  ta_review_pending: 'TA Review Pending',
  mis_report_assign_pending: 'MIS Report Assign Pending',
};
const PENDING_STATUSES = Object.keys(PENDING_LABELS);
const ENGINEER_STATUSES = ['field_work_in_progress', 'data_uploaded', 'field_work_completed'];

const PROGRESS_LABELS: Record<string, string> = {
  field_work_completed: 'Survey Done',
  ta_review_completed: 'Field Work Approved',
  report_completed: 'Report Completed',
  report_finalized: 'Report Finalized',
  report_sent_to_client: 'Report Sent',
};
const PROGRESS_COLUMNS = Object.keys(PROGRESS_LABELS);

const REJECT_LABELS: Record<string, string> = {
  ta_rejected: 'TA Rejected',
  report_maker_rejected: 'Report Maker Rejected',
  validation_rejected: 'Validation Rejected',
};
const REJECT_STATUSES = Object.keys(REJECT_LABELS);

// Pivot flat grouped rows ({ '<name field>': x, survey_workflow_status, count }) into
// one row per dimension with a column per workflow status.
const pivotLevel = (
  rows: any[] | undefined,
  nameKeys: string[],
  statuses: string[]
): Array<{ name: string } & Record<string, number>> => {
  const map = new Map<string, { name: string } & Record<string, number>>();
  for (const r of rows || []) {
    const name = nameKeys.map((k) => r?.[k]).find((v) => v != null && v !== '') || 'Unassigned';
    const key = String(name);
    const existing = map.get(key) || ({ name: key } as { name: string } & Record<string, number>);
    const status = String(r?.survey_workflow_status ?? '');
    existing[status] = (existing[status] || 0) + toNumber(r?.count);
    map.set(key, existing);
  }
  return Array.from(map.values()).map((row) => {
    statuses.forEach((s) => {
      if (row[s] == null) row[s] = 0;
    });
    return row;
  });
};

const levelColumns = (firstLabel: string, statuses: string[], labels: Record<string, string>) => [
  { key: 'name', label: firstLabel },
  ...statuses.map((s) => ({ key: s, label: labels[s] ?? s, align: 'center' as const })),
];

interface TechnicalAssociateViewProps {
  data: TechnicalAssociateDashboard;
}

export const TechnicalAssociateView: React.FC<TechnicalAssociateViewProps> = ({ data }) => {
  const d = data as any;
  const { summary, pending_sites, quality_metrics } = data;

  const responsibility = d.responsibility_overview;
  const approvals = d.approvals;
  const pendingWork = d.pending_work;
  const todayProgress = d.today_progress;
  const yesterdayProductivity = d.yesterday_productivity;
  const rejectionPending = d.rejection_pending;
  const referenceLists = d.reference_lists;

  const pendingActivity = pivotLevel(pendingWork?.activity_level, ['activity.activity_name'], PENDING_STATUSES);
  const pendingProject = pivotLevel(pendingWork?.project_level, ['project.project_short_name', 'project.project_name'], PENDING_STATUSES);
  const pendingEngineer = pivotLevel(pendingWork?.user_level?.engineer_wise, ['site_engineer_username'], ENGINEER_STATUSES);
  const rejectActivity = pivotLevel(rejectionPending?.activity_level, ['activity.activity_name'], REJECT_STATUSES);
  const rejectProject = pivotLevel(rejectionPending?.project_level, ['project.project_short_name', 'project.project_name'], REJECT_STATUSES);

  // small renderer for the two identical progress blocks (Today / Yesterday)
  const renderProgress = (block: any, title: string, icon: any) => {
    if (!block) return null;
    const gs = block.group_stage || {};
    const progressCols = levelColumns('', PROGRESS_COLUMNS, PROGRESS_LABELS);
    return (
      <div className="space-y-6">
        <SectionCard title={`${title} — Group Stage`} subtitle={formatDate(block.date)} icon={icon}>
          <div className="grid grid-cols-2 sm:grid-cols-5 gap-3">
            <MiniStat label="Survey Done" value={gs.survey_done ?? 0} color="blue" />
            <MiniStat label="Field Work Approved" value={gs.field_work_approved ?? 0} color="green" />
            <MiniStat label="Report Completed" value={gs.report_completed ?? 0} color="yellow" />
            <MiniStat label="Report Finalized" value={gs.report_finalized ?? 0} color="green" />
            <MiniStat label="Report Sent" value={gs.report_sent ?? 0} color="green" />
          </div>
        </SectionCard>

        {((block.activity_level?.length ?? 0) > 0 || (block.project_level?.length ?? 0) > 0) && (
          <div className="grid grid-cols-1 xl:grid-cols-2 gap-6">
            {(block.activity_level?.length ?? 0) > 0 && (
              <SectionCard title={`${title} — Activity Level`} icon={FaProjectDiagram}>
                <DataTable columns={[{ key: 'activity_name', label: 'Activity' }, ...progressCols.slice(1)]} data={block.activity_level} maxHeight="300px" emptyMessage="No progress" />
              </SectionCard>
            )}
            {(block.project_level?.length ?? 0) > 0 && (
              <SectionCard title={`${title} — Project Level`} icon={FaBuilding}>
                <DataTable columns={[{ key: 'project_name', label: 'Project' }, ...progressCols.slice(1)]} data={block.project_level} maxHeight="300px" emptyMessage="No progress" />
              </SectionCard>
            )}
          </div>
        )}

        {block.user_level && (
          <div className="grid grid-cols-1 lg:grid-cols-2 gap-6">
            <SectionCard title={`${title} — TA Approvals`} icon={FaUserTie}>
              <DataTable
                columns={[{ key: 'name', label: 'TA' }, { key: 'count', label: 'Approved', align: 'center' }]}
                data={block.user_level.ta_wise ?? []}
                maxHeight="240px"
                emptyMessage="No approvals"
              />
            </SectionCard>
            <SectionCard title={`${title} — Engineer Completions`} icon={FaMapMarkerAlt}>
              <DataTable
                columns={[{ key: 'name', label: 'Engineer' }, { key: 'count', label: 'Completed', align: 'center' }]}
                data={block.user_level.engineer_wise ?? []}
                maxHeight="240px"
                emptyMessage="No completions"
              />
            </SectionCard>
          </div>
        )}
      </div>
    );
  };

  return (
    <div className="space-y-6">
      {/* ===== Responsibility Overview ===== */}
      {responsibility && (
        <SectionCard title="Responsibility Overview" icon={FaProjectDiagram}>
          <div className="grid grid-cols-2 md:grid-cols-4 lg:grid-cols-8 gap-3">
            <StatCard title="Assigned Projects" value={responsibility.assigned_projects ?? 0} icon={FaProjectDiagram} iconBgColor="bg-blue-100" iconColor="text-blue-600" />
            <StatCard title="Direct Team" value={responsibility.direct_team ?? 0} icon={FaUsers} iconBgColor="bg-indigo-100" iconColor="text-indigo-600" />
            <StatCard title="All Team" value={responsibility.all_team ?? 0} icon={FaUsers} iconBgColor="bg-violet-100" iconColor="text-violet-600" />
            <StatCard title="TA Reviewed (Month)" value={responsibility.ta_review_completed_current_month ?? 0} icon={FaCheckDouble} iconBgColor="bg-green-100" iconColor="text-green-600" />
            <StatCard title="TA Review Pending" value={responsibility.ta_review_pending ?? 0} icon={FaHourglassHalf} iconBgColor="bg-yellow-100" iconColor="text-yellow-600" />
            <StatCard title="Field Work In Progress" value={responsibility.field_work_in_progress ?? 0} icon={FaCog} iconBgColor="bg-purple-100" iconColor="text-purple-600" />
            <StatCard title="Site Data WIP" value={responsibility.site_data_wip ?? 0} icon={FaFileAlt} iconBgColor="bg-teal-100" iconColor="text-teal-600" />
            <StatCard title="Field Upload Pending" value={responsibility.field_data_upload_pending ?? 0} icon={FaHourglassHalf} iconBgColor="bg-orange-100" iconColor="text-orange-600" />
          </div>
        </SectionCard>
      )}

      {/* ===== Approvals ===== */}
      <ApprovalsSection approvals={approvals} />

      {/* ===== Summary (kept) ===== */}
      <div className="grid grid-cols-2 md:grid-cols-4 lg:grid-cols-7 gap-4">
        <StatCard title="Total Assigned" value={summary.total_assigned} icon={FaClipboardCheck} iconBgColor="bg-blue-100" iconColor="text-blue-600" />
        <StatCard title="Pending Review" value={summary.pending_review} icon={FaHourglassHalf} iconBgColor="bg-yellow-100" iconColor="text-yellow-600" />
        <StatCard title="Reviewed" value={summary.reviewed_total} icon={FaCheckDouble} iconBgColor="bg-green-100" iconColor="text-green-600" />
        <StatCard title="Approved" value={summary.approved} icon={FaCheckDouble} iconBgColor="bg-green-100" iconColor="text-green-600" />
        <StatCard title="Rejected" value={summary.rejected} icon={FaExclamationTriangle} iconBgColor="bg-red-100" iconColor="text-red-600" />
        <StatCard title="This Month" value={summary.reviewed_this_month} icon={FaCalendarCheck} iconBgColor="bg-purple-100" iconColor="text-purple-600" />
        <StatCard title="Approval Rate" value={`${summary.approval_rate.toFixed(1)}%`} icon={FaChartLine} iconBgColor="bg-indigo-100" iconColor="text-indigo-600" />
      </div>

      {/* ===== Operations Overview → Pending Work ===== */}
      {pendingWork && (
        <SectionCard title="Pending Work — Group Stage" icon={FaHourglassHalf}>
          <div className="grid grid-cols-1 sm:grid-cols-2 gap-3">
            <MiniStat label="Field Data Upload Pending" value={pendingWork.group_stage?.field_data_upload_pending ?? 0} color="blue" />
            <MiniStat label="Report Submission Pending" value={pendingWork.group_stage?.report_submission_pending ?? 0} color="yellow" />
          </div>
          {pendingWork.by_workflow_status && (
            <div className="mt-4 h-64">
              <BarChart
                labels={Object.keys(pendingWork.by_workflow_status).map((k) => PENDING_LABELS[k] ?? k)}
                datasets={[{ label: 'Sites', data: Object.values(pendingWork.by_workflow_status).map((v) => toNumber(v)) }]}
                horizontal
                showLegend={false}
              />
            </div>
          )}
        </SectionCard>
      )}

      {pendingWork && (pendingActivity.length > 0 || pendingProject.length > 0) && (
        <div className="grid grid-cols-1 xl:grid-cols-2 gap-6">
          {pendingActivity.length > 0 && (
            <SectionCard title="Pending Work — Activity Level" icon={FaProjectDiagram}>
              <DataTable columns={levelColumns('Activity', PENDING_STATUSES, PENDING_LABELS)} data={pendingActivity} maxHeight="320px" emptyMessage="No pending sites" />
            </SectionCard>
          )}
          {pendingProject.length > 0 && (
            <SectionCard title="Pending Work — Project Level" icon={FaBuilding}>
              <DataTable columns={levelColumns('Project', PENDING_STATUSES, PENDING_LABELS)} data={pendingProject} maxHeight="320px" emptyMessage="No pending sites" />
            </SectionCard>
          )}
        </div>
      )}

      {pendingEngineer.length > 0 && (
        <SectionCard title="Pending Work — Engineer Level" subtitle="Field stages by engineer" icon={FaMapMarkerAlt}>
          <DataTable columns={levelColumns('Engineer', ENGINEER_STATUSES, PENDING_LABELS)} data={pendingEngineer} maxHeight="320px" emptyMessage="No engineer field work pending" />
        </SectionCard>
      )}

      {/* ===== Today Progress ===== */}
      {renderProgress(todayProgress, "Today's Progress", FaCheckDouble)}

      {/* ===== Productivity → Completed Yesterday ===== */}
      {renderProgress(yesterdayProductivity, 'Productivity (Yesterday)', FaCalendarCheck)}

      {/* ===== Rejection Pending ===== */}
      {rejectionPending && (
        <SectionCard title="Rejection Pending — Workflow Stage" icon={FaExclamationTriangle}>
          <div className="grid grid-cols-1 sm:grid-cols-3 gap-3">
            <MiniStat label="TA Rejected" value={rejectionPending.by_workflow_status?.ta_rejected ?? 0} color="red" />
            <MiniStat label="Report Maker Rejected" value={rejectionPending.by_workflow_status?.report_maker_rejected ?? 0} color="red" />
            <MiniStat label="Validation Rejected" value={rejectionPending.by_workflow_status?.validation_rejected ?? 0} color="red" />
          </div>
        </SectionCard>
      )}

      {rejectionPending && (rejectActivity.length > 0 || rejectProject.length > 0) && (
        <div className="grid grid-cols-1 xl:grid-cols-2 gap-6">
          {rejectActivity.length > 0 && (
            <SectionCard title="Rejections — Activity Level" icon={FaProjectDiagram}>
              <DataTable columns={levelColumns('Activity', REJECT_STATUSES, REJECT_LABELS)} data={rejectActivity} maxHeight="280px" emptyMessage="No rejections" />
            </SectionCard>
          )}
          {rejectProject.length > 0 && (
            <SectionCard title="Rejections — Project Level" icon={FaBuilding}>
              <DataTable columns={levelColumns('Project', REJECT_STATUSES, REJECT_LABELS)} data={rejectProject} maxHeight="280px" emptyMessage="No rejections" />
            </SectionCard>
          )}
        </div>
      )}

      {rejectionPending?.user_level && (
        <div className="grid grid-cols-1 lg:grid-cols-3 gap-6">
          <SectionCard title="Report Makers — Rejected" icon={FaUserTie}>
            <DataTable
              columns={[{ key: 'report_designer_username', label: 'Report Maker' }, { key: 'count', label: 'Rejected', align: 'center' }]}
              data={rejectionPending.user_level.report_maker_wise ?? []}
              maxHeight="240px"
              emptyMessage="No report-maker rejections"
            />
          </SectionCard>
          <SectionCard title="Validators — Rejected" icon={FaUserTie}>
            <DataTable
              columns={[{ key: 'validator_username', label: 'Validator' }, { key: 'count', label: 'Rejected', align: 'center' }]}
              data={rejectionPending.user_level.validator_wise ?? []}
              maxHeight="240px"
              emptyMessage="No validation rejections"
            />
          </SectionCard>
          <SectionCard title="TA — Rejected" icon={FaUserTie}>
            <DataTable
              columns={[{ key: 'ta_username', label: 'TA' }, { key: 'count', label: 'Rejected', align: 'center' }]}
              data={rejectionPending.user_level.ta_wise ?? []}
              maxHeight="240px"
              emptyMessage="No TA rejections"
            />
          </SectionCard>
        </div>
      )}

      {/* ===== Kept: review queue + quality metrics ===== */}
      <div className="grid grid-cols-1 lg:grid-cols-2 gap-6">
        <SectionCard title="Pending Sites for Review" icon={FaHourglassHalf}>
          <DataTable
            columns={[
              { key: 'site_id', label: 'Site ID' },
              { key: 'site_name', label: 'Name' },
              { key: 'engineer', label: 'Engineer' },
              { key: 'priority', label: 'Priority', render: (item) => <StatusBadge status={item.priority} /> },
              { key: 'work_date', label: 'Work Date', render: (item) => formatDate(item.work_date) },
            ]}
            data={pending_sites}
            maxHeight="320px"
            emptyMessage="No pending sites for review"
          />
        </SectionCard>

        <SectionCard title="Quality Metrics" icon={FaChartLine}>
          <div className="space-y-6">
            <div className="flex items-center justify-center">
              <ProgressRing value={quality_metrics.average_rating} maxValue={5} label="Average Rating" size="lg" color="#22c55e" />
            </div>
            <div className="h-48">
              <BarChart
                labels={quality_metrics.feedback_distribution.map((f) => f.ta_se_quality_feedback)}
                datasets={[{ label: 'Feedback Count', data: quality_metrics.feedback_distribution.map((f) => f.count) }]}
                showLegend={false}
              />
            </div>
          </div>
        </SectionCard>
      </div>

      {/* ===== Reference lists ===== */}
      {referenceLists && (
        <div className="grid grid-cols-1 lg:grid-cols-3 gap-6">
          <SectionCard title="Projects" icon={FaProjectDiagram}>
            <DataTable
              columns={[
                { key: 'project_short_name', label: 'Code' },
                { key: 'project_name', label: 'Project' },
              ]}
              data={referenceLists.project_names ?? []}
              maxHeight="240px"
              emptyMessage="No projects"
            />
          </SectionCard>
          <SectionCard title="MIS" icon={FaUserTie}>
            <DataTable
              columns={[
                { key: 'username', label: 'MIS' },
                { key: 'sites', label: 'Sites', align: 'center' },
              ]}
              data={referenceLists.mis_names ?? []}
              maxHeight="240px"
              emptyMessage="No MIS users"
            />
          </SectionCard>
          <SectionCard title="Last 10 Sites Completed" icon={FaCheckDouble}>
            <DataTable
              columns={[
                { key: 'site_id', label: 'Site ID' },
                { key: 'site_name', label: 'Name' },
                { key: 'ta_work_date', label: 'Reviewed', render: (item) => formatDate(item.ta_work_date) },
              ]}
              data={referenceLists.last_10_completed ?? []}
              maxHeight="240px"
              emptyMessage="No completed sites"
            />
          </SectionCard>
        </div>
      )}
    </div>
  );
};

export default TechnicalAssociateView;